import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useOrgCustomerId } from './org-view';
import { sameId } from './org-workspaces';
import { useIamProjects } from './queries';
import { useSharedProjects, type SharedProject } from './shared-projects';
import { iamProjectStatus, type IamProject } from './types';
import type { StoredProject, UsableWorkingProject } from './working-project-snapshot';

/**
 * localStorage key of the project the person is working in: `{"v":1,"p":"<projectId>","o":"<organizationId>"}`.
 * The key before it (`margarita.workingProjectId`, a bare project id) is never read as a choice; the first write removes it.
 */
export const WORKING_PROJECT_KEY = 'margarita.workingProject';
const OLD_WORKING_PROJECT_KEY = 'margarita.workingProjectId';
const CHANGED = 'margarita:working-project';

/** The stored value as a choice; a missing half, another version or unparseable text is "all projects" (null). */
export function parseStoredProject(raw: string | null | undefined): StoredProject | null {
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    const { v: version, p, o } = v as Record<string, unknown>;
    if (version !== 1 || typeof p !== 'string' || typeof o !== 'string' || !p || !o) return null;
    return { projectId: p, organizationId: o };
  } catch {
    return null;
  }
}

/** Writes the choice (null = all projects) and drops the key of the old format. */
export function storeProject(next: StoredProject | null, storage: Pick<Storage, 'setItem' | 'removeItem'>): void {
  if (next) storage.setItem(WORKING_PROJECT_KEY, JSON.stringify({ v: 1, p: next.projectId, o: next.organizationId }));
  else storage.removeItem(WORKING_PROJECT_KEY);
  storage.removeItem(OLD_WORKING_PROJECT_KEY);
}

function read(): string {
  try {
    return globalThis.localStorage?.getItem(WORKING_PROJECT_KEY) || '';
  } catch {
    return ''; // storage blocked: behave as if no project were chosen
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener('storage', onChange); // another tab
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** The stored choice, if it is still one of `usableIds`; a project archived or gone since is not "working" any more. */
export function resolveWorkingProject(stored: string, usableIds: readonly string[]): string {
  return stored && usableIds.includes(stored) ? stored : '';
}

/**
 * What the top bar does with the stored choice: the project itself while it is usable or while either list that could
 * contain it is still loading or failed, '' only once both lists loaded and it is in neither (archived, access revoked).
 */
export function resolveStoredProject(args: {
  stored: string;
  ownLoaded: boolean;
  /** true only when the shared list loaded successfully (false while loading and after an error) */
  sharedLoaded: boolean;
  ids: readonly string[];
}): string {
  const { stored, ownLoaded, sharedLoaded, ids } = args;
  if (!stored) return '';
  if (!ownLoaded || !sharedLoaded) return stored;
  return resolveWorkingProject(stored, ids);
}

/**
 * THE rule for "the project the person chose is still usable". It is usable only if it is one of their own projects and
 * the stored organization is the home one, or it is in the shared list under that very organization. Until both lists
 * loaded (and not after one failed) nothing is decided: `settled` is false and the choice is returned as stored.
 */
export function usableStoredProject(args: {
  stored: StoredProject | null;
  home: string | undefined;
  own: readonly { id: string }[];
  ownLoaded: boolean;
  shared: readonly Pick<SharedProject, 'project_id' | 'customer_id'>[];
  /** true only when the shared list loaded successfully */
  sharedLoaded: boolean;
}): UsableWorkingProject {
  const { stored, home, own, ownLoaded, shared, sharedLoaded } = args;
  const settled = ownLoaded && sharedLoaded && !!home;
  if (!stored) return { project: null, settled };
  // An own project of the home organization is confirmed by the own list alone; a failed or slow shared list does not hold it up.
  if (ownLoaded && sameId(stored.organizationId, home) && own.some((p) => p.id === stored.projectId)) return { project: stored, settled: true };
  const ids = [
    ...(sameId(stored.organizationId, home) ? own.map((p) => p.id) : []),
    ...shared.filter((p) => sameId(p.customer_id, stored.organizationId)).map((p) => p.project_id),
  ];
  const kept = resolveStoredProject({ stored: stored.projectId, ownLoaded: settled, sharedLoaded: settled, ids });
  return { project: kept ? stored : null, settled };
}

/**
 * Whether the person still reaches `organizationId` through some project once the lists have loaded: one of their own
 * active projects when it is the home organization, or any project of it in the shared list. Revoking one project of an
 * organization does not take the organization away while another project (or an organization-level role, which lists every
 * project) is left.
 */
export function organizationStillReachable(args: {
  organizationId: string;
  home: string | undefined;
  own: readonly { id: string }[];
  shared: readonly Pick<SharedProject, 'customer_id'>[];
}): boolean {
  const { organizationId, home, own, shared } = args;
  if (!organizationId) return false;
  if (sameId(organizationId, home) && own.length > 0) return true;
  return shared.some((p) => sameId(p.customer_id, organizationId));
}

/** The id of a checked choice, '' while it is not settled or when nothing (usable) is chosen: what a form may pre-fill from. */
export function settledProjectId(usable: UsableWorkingProject): string {
  return usable.settled && usable.project ? usable.project.projectId : '';
}

/** Everything `useWorkingProject` knows, from plain values: the stored text, the home organization and the two lists. */
export function workingProjectState(args: {
  raw: string;
  home: string | undefined;
  ownProjects: readonly Pick<IamProject, 'id' | 'status'>[];
  ownLoaded: boolean;
  shared: readonly Pick<SharedProject, 'project_id' | 'customer_id'>[];
  sharedLoaded: boolean;
}) {
  const stored = parseStoredProject(args.raw);
  const own = args.ownProjects.filter((p) => iamProjectStatus(p) === 'active');
  return { ...usableStoredProject({ stored, home: args.home, own, ownLoaded: args.ownLoaded, shared: args.shared, sharedLoaded: args.sharedLoaded }), stored };
}

/**
 * The project the person chose in the top bar, checked against the lists (`project` null = all projects), whether that
 * check could be made, and a setter that every open page sees (null = all projects).
 */
export function useWorkingProject() {
  const raw = useSyncExternalStore(subscribe, read, () => '');
  const home = useOrgCustomerId();
  const ownQ = useIamProjects(undefined, false, { enabled: true });
  const sharedQ = useSharedProjects();
  const state = useMemo(
    () =>
      workingProjectState({
        raw,
        home,
        ownProjects: ownQ.data ?? [],
        ownLoaded: ownQ.isSuccess,
        shared: sharedQ.data ?? [],
        sharedLoaded: sharedQ.isSuccess,
      }),
    [raw, home, ownQ.data, ownQ.isSuccess, sharedQ.data, sharedQ.isSuccess],
  );
  const choose = useCallback((next: StoredProject | null) => {
    try {
      if (globalThis.localStorage) storeProject(next, globalThis.localStorage);
    } catch {
      /* storage blocked: the choice lasts only until the page reloads, nothing else breaks */
    }
    window.dispatchEvent(new Event(CHANGED));
  }, []);
  return { ...state, choose };
}

/**
 * The other tab changed the project (a `storage` event on the working-project key). The open page is out of step when it is
 * a workspace of an organization other than the new choice's. "All projects" (no new value) or a page outside a workspace
 * contradicts nothing, and neither does an event for another key.
 */
export function projectChangedInOtherTab(args: {
  key: string | null;
  newValue: string | null;
  /** The organization whose workspace the open page is in; undefined outside any workspace. */
  openOrganizationId: string | undefined;
}): boolean {
  if (args.key !== WORKING_PROJECT_KEY || !args.openOrganizationId) return false;
  const next = parseStoredProject(args.newValue);
  return !!next && !sameId(next.organizationId, args.openOrganizationId);
}

/**
 * The notice after a `storage` event: it stays shown once shown (an unrelated key, a null key or "All projects" from the
 * other tab does not take it back) and appears only for a change that contradicts the open workspace.
 */
export function tabNoticeAfterEvent(args: {
  shown: boolean;
  key: string | null;
  newValue: string | null;
  openOrganizationId: string | undefined;
}): boolean {
  return args.shown || projectChangedInOtherTab(args);
}
